import { useMemo } from "react";
import { resolveScopeShopIds, type Shop, useAuthStore } from "@/stores/auth";

export interface ShopScope {
  /** Effective analytics scope — sorted for stable query keys. */
  scopeShopIds: string[];
  /** All shops of the active workspace (the scope's universe). */
  workspaceShops: Shop[];
  /** True when the scope covers every workspace shop ("all shops"). */
  isAll: boolean;
}

/**
 * ADP-110: the analytics shop scope for the active workspace. Defaults to all
 * shops; a persisted subset narrows it. This drives the aggregated dashboard
 * reads only — operational pages keep `currentShopId`.
 */
export function useShopScope(): ShopScope {
  const user = useAuthStore((s) => s.user);
  const shops = useAuthStore((s) => s.shops);
  const selectedShopIdsByWorkspace = useAuthStore((s) => s.selectedShopIdsByWorkspace);

  return useMemo(() => {
    const activeWs = user?.activeWorkspaceId ?? null;
    const workspaceShops = shops.filter((s) => s.workspaceId === activeWs);
    const scopeShopIds = [...resolveScopeShopIds(user, shops, selectedShopIdsByWorkspace)].sort();
    return {
      scopeShopIds,
      workspaceShops,
      isAll: scopeShopIds.length === workspaceShops.length,
    };
  }, [user, shops, selectedShopIdsByWorkspace]);
}
